import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Download, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useState } from 'react';

interface Invoice {
  id: string;
  amount: number;
  due_date: string;
  status: 'pending' | 'paid' | 'overdue' | 'cancelled';
  paid_at: string | null;
  description: string | null;
  external_id: string | null;
}

interface ClienteRecebimentosTabProps {
  customerId: string;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value / 100);
}

const statusColors: Record<string, string> = {
  pending: 'bg-[hsl(var(--basic-100))] text-[hsl(var(--basic-700))]',
  paid: 'bg-[hsl(var(--accent-100))] text-[hsl(var(--accent-700))]',
  overdue: 'bg-[hsl(var(--careful-100))] text-[hsl(var(--careful-700))]',
  cancelled: 'bg-muted text-muted-foreground'
};

const statusLabels: Record<string, string> = {
  pending: 'Em aberto',
  paid: 'Recebido',
  overdue: 'Vencido',
  cancelled: 'Cancelado'
};

export function ClienteRecebimentosTab({ customerId }: ClienteRecebimentosTabProps) {
  const [search, setSearch] = useState('');

  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['customer-payments', customerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('customer_id', customerId)
        .eq('status', 'paid')
        .order('paid_at', { ascending: false });
      
      if (error) throw error;
      return data as Invoice[];
    }
  });

  const filteredInvoices = invoices.filter(inv => 
    !search || 
    inv.description?.toLowerCase().includes(search.toLowerCase()) ||
    inv.external_id?.includes(search)
  );

  const totalReceived = invoices.reduce((sum, inv) => sum + inv.amount, 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Pesquisar recebimentos..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button variant="outline" className="gap-2">
          <Download className="h-4 w-4" />
          XLS
        </Button>
      </div>

      {/* Summary */}
      <div className="flex justify-between items-center py-3 px-4 bg-[hsl(var(--accent-50))] rounded-lg border border-[hsl(var(--accent-200))]">
        <span className="text-sm text-[hsl(var(--accent-700))]">
          Total de recebimentos: <strong>{invoices.length}</strong>
        </span>
        <span className="text-sm font-bold text-[hsl(var(--accent-700))]">
          {formatCurrency(totalReceived)}
        </span>
      </div>

      {/* Table */}
      <div className="bg-card rounded-lg border overflow-hidden">
        <div className="grid grid-cols-[1fr_1.5fr_1fr_1fr_8rem] gap-4 p-4 bg-muted/50 border-b font-medium text-sm text-muted-foreground">
          <span>Valor</span>
          <span>ID Cobrança</span>
          <span>Emissão</span>
          <span>Recebido em</span>
          <span>Status</span>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-muted-foreground">Carregando...</div>
        ) : filteredInvoices.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground italic">
            Nenhum recebimento encontrado.
          </div>
        ) : (
          <div className="divide-y">
            {filteredInvoices.map((invoice) => (
              <div 
                key={invoice.id} 
                className="grid grid-cols-[1fr_1.5fr_1fr_1fr_8rem] gap-4 p-4 hover:bg-muted/30 transition-colors items-center"
              >
                <span className="font-bold text-[hsl(var(--basic-900))]">
                  {formatCurrency(invoice.amount)}
                </span>
                <span className="font-mono text-sm text-muted-foreground truncate">
                  {invoice.external_id || invoice.id.slice(0, 14)}
                </span>
                <span className="text-sm text-muted-foreground">
                  {new Date(invoice.due_date).toLocaleDateString('pt-BR')}
                </span>
                <span className="text-sm text-[hsl(var(--basic-700))]">
                  {invoice.paid_at 
                    ? new Date(invoice.paid_at).toLocaleDateString('pt-BR')
                    : '-'
                  }
                </span>
                <span className={`px-2 py-1 rounded text-xs font-bold uppercase text-center ${statusColors[invoice.status]}`}>
                  {statusLabels[invoice.status]}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
